import mapboxgl from 'mapbox-gl';
import { RouteLocationNormalizedLoaded, RouteRecordName } from 'vue-router';
import { useWindowSize, watchOnce } from '@vueuse/core';
import { getBreakpoint } from '@/../uno-preset/preset-theme';
import { ISbStoryData } from '@storyblok/vue';
import { TemplateMapLocationStoryblok } from '@/types';
import { MaybeRefOrGetter } from 'vue';

const mobileBreakpoint = getBreakpoint('tl')
const { width: windowWidth, height: windowHeight } = useWindowSize();

export default function useMapPaddingAndCenter(map: Ref<mapboxgl.Map | null | any>, currentRoute: Ref<RouteLocationNormalizedLoaded>, currentLocData: MaybeRefOrGetter<ISbStoryData<TemplateMapLocationStoryblok> | undefined>) {
  // trigger if: map is loaded
  watchOnce(map, (_map) => {
    _map!.on('load', () => {
      if (!_map) return;
      updatePaddingAndCenter(_map, currentRoute.value.name, currentLocData);
    });
  })

  // trigger if: route changed
  watch(
    () => currentRoute.value.name,
    (name) => {
      if (!map.value) return;
      updatePaddingAndCenter(map.value, name, currentLocData);
    },
    { immediate: true }
  );
  
  // trigger if: window width changed
  watch(windowWidth, () => {
    if (!map.value) return;
    updatePaddingAndCenter(map.value, currentRoute.value.name, currentLocData);
  })

  function updatePaddingAndCenter(map: mapboxgl.Map, routeName: RouteRecordName | null | undefined, currentLocData: MaybeRefOrGetter<ISbStoryData<TemplateMapLocationStoryblok> | undefined>) {
    const currentLocDataVal = toValue(currentLocData);  
    const isMobile = windowWidth.value < mobileBreakpoint;
    
    let currentCenter = map.getCenter();
    let center = {
      lng: currentCenter?.lng,
      lat: currentCenter?.lat,
    };
    let padding = {
      left: isMobile ? 50 : 200,
      right: isMobile ? 50 : 200,
      top: isMobile ? 150 : 250, // TODO: CALCULATE THE RIGHT PADDING BASED ON THE HEADER HEIGHT
      bottom: isMobile ? 100 : 200, // TODO: CALCULATE THE RIGHT PADDING BASED ON THE SEARCH BAR HEIGHT
    }

    // NB: on mobile the padding cannot be more than half of the window height

    switch (routeName) {
      case 'MapLocation':
        if (isMobile) {
          padding.bottom = windowHeight.value * 0.5;
        } else {
          padding.left = windowWidth.value * 0.3;
        }
        center.lng = parseFloat(currentLocDataVal?.content.longitude || '0');
        center.lat = parseFloat(currentLocDataVal?.content.latitude || '0');
        break;
      case 'MapOverview':
      default:
        break;
    }
  
    map.easeTo({
      padding,
      center,
      duration: 1000, // In ms. This matches the CSS transition duration property.
    });
  };

  return {
    updatePaddingAndCenter,
  };
}
